import { useState, useEffect, useCallback, useRef } from 'react';
import {
  savePendingBooking,
  getPendingBookings,
  getPendingBookingsCount,
  updateBookingStatus,
  removePendingBooking,
  PendingBooking,
  initDB,
} from '../utils/offlineStorage';
import { PWAUtils } from '../utils/pwa';

// Booking data interface for creating offline bookings
export interface OfflineBookingData {
  barbershopId: number;
  barberId: number;
  services: number[];
  date: string;
  time: string;
  notes?: string;
  userId?: string;
  totalPrice?: number;
}

// Sync result interface
export interface SyncResult {
  success: boolean;
  synced: number;
  failed: number;
  errors: Array<{ bookingId: string; error: string }>;
}

// Hook return type
export interface UseOfflineBookingReturn {
  isOnline: boolean;
  pendingCount: number;
  isSyncing: boolean;
  lastSyncTime: Date | null;
  createOfflineBooking: (booking: OfflineBookingData) => Promise<string>;
  syncPendingBookings: () => Promise<SyncResult>;
  getPendingBookingsList: () => Promise<PendingBooking[]>;
  cancelPendingBooking: (id: string) => Promise<void>;
  retryFailedBookings: () => Promise<SyncResult>;
}

// Maximum retry attempts for failed bookings
const MAX_RETRY_ATTEMPTS = 3;

// Sync debounce delay in milliseconds
const SYNC_DEBOUNCE_DELAY = 2000;

/**
 * Hook for managing offline booking functionality
 * - Detects online/offline status
 * - Queues bookings when offline
 * - Syncs when back online
 * - Shows pending booking count
 * - Provides offline booking creation function
 */
export const useOfflineBooking = (): UseOfflineBookingReturn => {
  const [isOnline, setIsOnline] = useState<boolean>(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  );
  const [pendingCount, setPendingCount] = useState<number>(0);
  const [isSyncing, setIsSyncing] = useState<boolean>(false);
  const [lastSyncTime, setLastSyncTime] = useState<Date | null>(null);

  const syncTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef<boolean>(true);

  /**
   * Update the pending booking count
   */
  const updatePendingCount = useCallback(async () => {
    try {
      const count = await getPendingBookingsCount();
      if (isMountedRef.current) {
        setPendingCount(count);
      }
    } catch (error) {
      console.error('Failed to get pending bookings count:', error);
    }
  }, []);

  /**
   * Get the auth token from localStorage
   */
  const getAuthToken = (): string | null => {
    try {
      const authData = localStorage.getItem('auth');
      if (authData) {
        const parsed = JSON.parse(authData);
        return parsed.accessToken || null;
      }
      return null;
    } catch {
      return null;
    }
  };

  /**
   * Sync a single booking to the server
   */
  const syncSingleBooking = useCallback(async (booking: PendingBooking): Promise<boolean> => {
    const token = getAuthToken();
    if (!token) {
      return false;
    }

    try {
      // Update status to syncing
      await updateBookingStatus(booking.id, 'syncing');

      // Construct the booking payload for the API
      const bookingPayload = {
        barbershopId: booking.barbershopId,
        barberId: booking.barberId,
        bookingDateTime: `${booking.date}T${booking.time}:00`,
        services: booking.services.map((serviceId) => ({ serviceId })),
        notes: booking.notes,
      };

      // Get API URL
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5256';

      const response = await fetch(`${apiUrl}/api/bookings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(bookingPayload),
      });

      if (response.ok) {
        // Successfully synced, remove from pending
        await removePendingBooking(booking.id);
        return true;
      } else {
        // Failed to sync, mark as failed
        await updateBookingStatus(booking.id, 'failed', true);
        return false;
      }
    } catch (error) {
      console.error('Failed to sync booking:', booking.id, error);
      await updateBookingStatus(booking.id, 'failed', true);
      return false;
    }
  }, []);

  /**
   * Sync all pending bookings
   */
  const syncPendingBookings = useCallback(async (): Promise<SyncResult> => {
    if (isSyncing || !isOnline) {
      return { success: false, synced: 0, failed: 0, errors: [] };
    }

    setIsSyncing(true);

    const result: SyncResult = {
      success: true,
      synced: 0,
      failed: 0,
      errors: [],
    };

    try {
      const pendingBookings = await getPendingBookings();
      const bookingsToSync = pendingBookings.filter(
        (booking) =>
          booking.status === 'pending' ||
          (booking.status === 'failed' && booking.retryCount < MAX_RETRY_ATTEMPTS)
      );

      for (const booking of bookingsToSync) {
        const success = await syncSingleBooking(booking);
        if (success) {
          result.synced++;
        } else {
          result.failed++;
          result.errors.push({
            bookingId: booking.id,
            error: 'Failed to sync booking',
          });
        }
      }

      result.success = result.failed === 0;
      setLastSyncTime(new Date());

      // Notify the service worker about sync completion
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'SYNC_COMPLETE',
          payload: result,
        });
      }
    } catch (error) {
      console.error('Sync failed:', error);
      result.success = false;
    } finally {
      if (isMountedRef.current) {
        setIsSyncing(false);
        await updatePendingCount();
      }
    }

    return result;
  }, [isOnline, isSyncing, updatePendingCount, syncSingleBooking]);

  /**
   * Create an offline booking
   */
  const createOfflineBooking = useCallback(
    async (bookingData: OfflineBookingData): Promise<string> => {
      try {
        // Initialize DB if not already done
        await initDB();

        // Save the booking to IndexedDB
        const bookingId = await savePendingBooking(bookingData);

        // Update the pending count
        await updatePendingCount();

        // If online, try to sync immediately (debounced)
        if (isOnline) {
          if (syncTimeoutRef.current) {
            clearTimeout(syncTimeoutRef.current);
          }
          syncTimeoutRef.current = setTimeout(() => {
            syncPendingBookings();
          }, SYNC_DEBOUNCE_DELAY);
        } else {
          // Schedule background sync if available
          PWAUtils.scheduleBackgroundSync('booking-sync');
        }

        return bookingId;
      } catch (error) {
        console.error('Failed to create offline booking:', error);
        throw error;
      }
    },
    [isOnline, syncPendingBookings, updatePendingCount]
  );

  /**
   * Get the list of pending bookings
   */
  const getPendingBookingsList = useCallback(async (): Promise<PendingBooking[]> => {
    try {
      return await getPendingBookings();
    } catch (error) {
      console.error('Failed to get pending bookings:', error);
      return [];
    }
  }, []);

  /**
   * Cancel a pending booking
   */
  const cancelPendingBooking = useCallback(
    async (id: string): Promise<void> => {
      try {
        await removePendingBooking(id);
        await updatePendingCount();
      } catch (error) {
        console.error('Failed to cancel pending booking:', error);
        throw error;
      }
    },
    [updatePendingCount]
  );

  /**
   * Retry failed bookings
   */
  const retryFailedBookings = useCallback(async (): Promise<SyncResult> => {
    if (!isOnline) {
      return { success: false, synced: 0, failed: 0, errors: [] };
    }

    setIsSyncing(true);

    const result: SyncResult = {
      success: true,
      synced: 0,
      failed: 0,
      errors: [],
    };

    try {
      const pendingBookings = await getPendingBookings();
      const failedBookings = pendingBookings.filter(
        (booking) => booking.status === 'failed' && booking.retryCount < MAX_RETRY_ATTEMPTS
      );

      for (const booking of failedBookings) {
        const success = await syncSingleBooking(booking);
        if (success) {
          result.synced++;
        } else {
          result.failed++;
          result.errors.push({
            bookingId: booking.id,
            error: `Failed after ${booking.retryCount + 1} attempts`,
          });
        }
      }

      result.success = result.failed === 0;
    } catch (error) {
      console.error('Retry failed:', error);
      result.success = false;
    } finally {
      if (isMountedRef.current) {
        setIsSyncing(false);
        await updatePendingCount();
      }
    }

    return result;
  }, [isOnline, updatePendingCount, syncSingleBooking]);

  // Handle online/offline status changes
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      // Auto-sync when coming back online
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
      syncTimeoutRef.current = setTimeout(() => {
        syncPendingBookings();
      }, SYNC_DEBOUNCE_DELAY);
    };

    const handleOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [syncPendingBookings]);

  // Handle service worker messages
  useEffect(() => {
    const handleServiceWorkerMessage = (event: MessageEvent) => {
      const { type } = event.data;

      if (type === 'BACKGROUND_SYNC_COMPLETE') {
        updatePendingCount();
        setLastSyncTime(new Date());
      }
    };

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
    }

    return () => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
      }
    };
  }, [updatePendingCount]);

  // Initialize
  useEffect(() => {
    isMountedRef.current = true;

    // Initialize IndexedDB and get initial pending count
    const initialize = async () => {
      try {
        await initDB();
        await updatePendingCount();

        // If online and there are pending bookings, try to sync
        if (navigator.onLine) {
          const count = await getPendingBookingsCount();
          if (count > 0) {
            syncTimeoutRef.current = setTimeout(() => {
              syncPendingBookings();
            }, SYNC_DEBOUNCE_DELAY);
          }
        }
      } catch (error) {
        console.error('Failed to initialize offline booking:', error);
      }
    };

    initialize();

    return () => {
      isMountedRef.current = false;
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
    };
  }, [updatePendingCount, syncPendingBookings]);

  return {
    isOnline,
    pendingCount,
    isSyncing,
    lastSyncTime,
    createOfflineBooking,
    syncPendingBookings,
    getPendingBookingsList,
    cancelPendingBooking,
    retryFailedBookings,
  };
};

export default useOfflineBooking;
